import CloseIcon from "@mui/icons-material/Close";
import { Box, Tooltip, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { ConfirmationDialog } from "../../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import { ReactComponent as CheckMarkCircle } from "../../../../common/icons/checkMarkCircle.svg";
import { ReactComponent as ErrorRed } from "../../../../common/icons/errorRed.svg";
import { canExpertEditEntries } from "../../../user/accessRightChecks";
import { Attachment, LeaveEntity } from "../../entriesAPI";
import {
  doRemoveLeaveAttachment,
  getLeaveAttachmentDownloadUrl,
  postResetEntryApprovalState,
  updateEntriesLeaveAttachment,
} from "../../entriesSlice";
import {
  ABSENCE_TYPES_REQUIRING_ATTACHMENT,
  hasApprovedEntries,
  hasEntriesNeedingApprovalReset,
  toApprovalResetPayload,
} from "./functions";
import { AbsenceListEntry } from "../Absences/AbsenceHoursList/AbsenceHoursList";
import FileDropzone from "../../../../common/components/functional/fileDropzone/FileDropzone";

interface Props {
  entries: AbsenceListEntry[];
  setEntries: (val: AbsenceListEntry[]) => void;
  leaveEntity: LeaveEntity;
  setLeaveEntity: (val: LeaveEntity) => void;
  pendingAttachments?: File[];
  onPendingAttachmentsChange?: (files: File[]) => void;
}

const LeaveAttachments = ({
  leaveEntity,
  setLeaveEntity,
  entries,
  setEntries,
  pendingAttachments,
  onPendingAttachmentsChange,
}: Props) => {
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    filename: string;
    id: string;
    size: number;
  }>({ isOpen: false, filename: "", id: "", size: 0 });

  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.user);
  const canUpload = user ? canExpertEditEntries(user) : false;

  const { t } = useTranslation();

  const handleRemoveAttachment = (
    filename: string,
    id: string,
    size: number,
  ) => {
    setConfirmDialog({ isOpen: true, filename, id, size });
  };

  const handleConfirmDeleteAttachment = async () => {
    const { filename, id, size } = confirmDialog;
    const entriesToReset = toApprovalResetPayload(checkedEntries);
    let resetEntriesById = new Map<
      string,
      { version: number; approvalState: "New" }
    >();

    if (entriesToReset.length > 0) {
      const resetAction = await dispatch(postResetEntryApprovalState(entriesToReset));
      if (!isFulfilled(resetAction)) {
        return;
      }

      resetEntriesById = new Map(
        resetAction.payload.map((entry) => [
          entry.id,
          {
            version: entry.version,
            approvalState: entry.payload.newState,
          },
        ]),
      );
    }

    dispatch(doRemoveLeaveAttachment({ filename, id })).then((action: any) => {
      if (isFulfilled(action)) {
        // Find correct event from response
        const event = action.payload.find(
          (event: any) =>
            event?.name === "lvAttRem" && event.id === leaveEntity.id,
        );

        const updatedLeaveEntity = {
          ...leaveEntity,
          version: event.version,
          leaveAttachment: {
            attachments: leaveEntity.leaveAttachment?.attachments?.filter(
              (attachment) => attachment.filename !== filename,
            ),
          },
        };

        leaveEntity?.leaveAttachment?.attachments &&
          event?.version &&
          setLeaveEntity({ ...updatedLeaveEntity });

        // Map through entries and update the specific entry that matches the id.
        const updatedEntries = entries.map((entry) => {
          const nextEntry =
            entry.leaveAttachment?.attachments
              ? {
                  ...entry,
                  leaveAttachment: {
                    ...entry.leaveAttachment,
                    attachments: entry.leaveAttachment.attachments.filter(
                      (attachment: Attachment) =>
                        !(
                          attachment.filename === filename &&
                          attachment.size === size
                        ),
                    ),
                  },
                }
              : entry;

          const resetEntry = resetEntriesById.get(entry.id);
          return resetEntry
            ? {
                ...nextEntry,
                version: resetEntry.version,
                approvalState: resetEntry.approvalState,
              }
            : nextEntry;
        });
        // Update the entries state.
        setEntries([...updatedEntries]);
        dispatch(
          updateEntriesLeaveAttachment(
            updatedEntries.map((e) => ({
              id: e.id,
              leaveAttachment: e.leaveAttachment,
            })),
          ),
        );
        setConfirmDialog({ isOpen: false, filename: "", id: "", size: 0 });
      }
    });
  };

  const handleAttachmentClick = async (
    talentId: string,
    pathspec: string,
    leaveId: string,
  ) => {
    dispatch(
      getLeaveAttachmentDownloadUrl({ pathspec, talentId, leaveId }),
    ).then((action: any) => {
      if (isFulfilled(action)) {
        window.open(action.payload.url, "_blank", "noopener,noreferrer");
      }
    });
  };

  const handleFileSelected = (file: File) => {
    onPendingAttachmentsChange?.([...(pendingAttachments ?? []), file]);
  };

  const handleRemovePendingAttachment = (index: number) => {
    const updated = (pendingAttachments ?? []).filter((_, i) => i !== index);
    onPendingAttachmentsChange?.(updated);
  };

  const checkedEntries = entries.filter((entry) => entry.checked);
  const shouldResetApproval = hasEntriesNeedingApprovalReset(checkedEntries);
  const hasApprovedCheckedEntries = hasApprovedEntries(checkedEntries);

  // leaveEntity.leaveAttachment is the authoritative source (fetched directly from LeaveEntry).
  // entries[x].leaveAttachment comes from work-time-entry projections which may lag behind.
  const allCheckedEntriesHaveLeaveAttachments =
    (leaveEntity?.leaveAttachment?.attachments?.length ?? 0) > 0 ||
    (checkedEntries.length > 0 &&
      checkedEntries.every(
        (entry) =>
          entry?.leaveAttachment?.attachments &&
          entry?.leaveAttachment?.attachments?.length > 0,
      ));

  // Check if the leave type is a sick leave type (has different localization)
  const isSickLeaveType = leaveEntity.type.includes("SICK_LEAVE");

  // Render attachment section only if the leave type requires it
  if (ABSENCE_TYPES_REQUIRING_ATTACHMENT.includes(leaveEntity.type)) {
    return leaveEntity?.id === null ? (
      <Box
        sx={{
          marginTop: "16px",
          padding: "8px",
          backgroundColor: "#F3F3F7",
        }}
      >
        {isSickLeaveType
          ? t("hourEntries:sickLeaveCertificateWillBeAsked")
          : t("hourEntries:certificateWillBeAsked")}
      </Box>
    ) : (
      <>
        {entries.length > 0 && (
          <Box
            sx={{
              marginTop: "16px",
              paddingLeft: "3px",
              fontSize: "12px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              sx={{
                marginRight: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {allCheckedEntriesHaveLeaveAttachments ? (
                <CheckMarkCircle
                  style={{
                    width: "16px",
                    height: "16px",
                  }}
                />
              ) : (
                <ErrorRed
                  style={{
                    width: "16px",
                    height: "16px",
                  }}
                />
              )}
            </Box>
            <Box>
              {
                // Look through entries and check if they have leaveAttachments
                entries &&
                entries.length > 0 &&
                allCheckedEntriesHaveLeaveAttachments
                  ? `${
                      isSickLeaveType
                        ? t("hourEntries:sickLeaveCertificateSubmitted")
                        : t("hourEntries:certificateSubmitted")
                    }.`
                  : `${
                      isSickLeaveType
                        ? t("hourEntries:sickLeaveCertificateNotSubmitted")
                        : t("hourEntries:certificateNotSubmitted")
                    }.`
              }
            </Box>
          </Box>
        )}
        {leaveEntity?.leaveAttachment?.attachments?.map((attachment, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: "#F3F3F7",
              padding: "5px 8px",
              borderRadius: "2px",
              marginTop: "16px",
            }}
          >
            <Tooltip
              title={attachment?.filename || ""}
              placement="top"
              enterDelay={500}
              enterNextDelay={500}
              leaveDelay={200}
            >
              <Box
                onClick={() =>
                  leaveEntity?.talentId &&
                  leaveEntity?.id &&
                  handleAttachmentClick(
                    leaveEntity.talentId,
                    attachment.pathspec,
                    leaveEntity.id,
                  )
                }
                sx={{
                  color: "rgba(137, 95, 223, 0.9)",
                  fontSize: "16px",
                  fontWeight: 600,
                  marginRight: "5px",
                  maxWidth: "220px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  cursor: "pointer",
                }}
              >
                {attachment.filename}
              </Box>
            </Tooltip>
            <Box
              sx={{
                marginLeft: "auto",
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              {`(${
                attachment.size >= 1000 * 1000
                  ? `${(attachment.size / (1000 * 1000)).toFixed(2)}MB`
                  : `${Math.round(attachment.size / 1000)}KB`
              })`}
            </Box>

            <CloseIcon
              onClick={() =>
                handleRemoveAttachment(
                  attachment.filename,
                  leaveEntity.id || "",
                  attachment.size,
                )
              }
              sx={{
                "&:hover": { cursor: "pointer" },
                marginLeft: "10px",
                height: "16px",
                width: "16px",
              }}
            />
          </Box>
        ))}
        {pendingAttachments?.map((pendingFile, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#F3F3F7",
              padding: "5px 8px",
              borderRadius: "2px",
              marginTop: "16px",
            }}
          >
            <Tooltip
              title={pendingFile.name || ""}
              placement="top"
              enterDelay={500}
              enterNextDelay={500}
              leaveDelay={200}
            >
              <Box
                sx={{
                  color: "rgba(137, 95, 223, 0.9)",
                  fontSize: "16px",
                  fontWeight: 600,
                  marginRight: "5px",
                  maxWidth: "260px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  cursor: "pointer",
                }}
              >
                {pendingFile.name}
              </Box>
            </Tooltip>
            <CloseIcon
              onClick={() => handleRemovePendingAttachment(index)}
              sx={{
                "&:hover": { cursor: "pointer" },
                marginLeft: "10px",
                height: "16px",
                width: "16px",
              }}
            />
          </Box>
        ))}
        {canUpload && leaveEntity.id && (
          <Box sx={{ marginTop: "8px" }}>
            <FileDropzone
              onFileSelected={handleFileSelected}
              accept=".pdf,.jpg,.jpeg,.png"
              label={t("hourEntries:uploadAttachment")}
              subLabel={t("hourEntries:clickToSelectFile")}
              sx={{ maxHeight: "80px" }}
            />
          </Box>
        )}
        <ConfirmationDialog
          title={t("hourEntries:absenceEntriesConfirmAttRemovalTitle")}
          contentText={
            <Box>
              <Typography>
                {t("hourEntries:absenceEntriesConfirmAttRemovalContext")}
              </Typography>
              {shouldResetApproval && (
                <>
                  <Typography mt={2}>
                    {t("hourEntries:resetApprovalDialogText1")}
                  </Typography>
                  {hasApprovedCheckedEntries && (
                    <Typography mt={1}>
                      {t("hourEntries:resetApprovalDialogText2")}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          }
          confirmText={t("hourEntries:entriesConfirmButton")}
          cancelText={t("hourEntries:entriesCancelButton")}
          isOpen={confirmDialog.isOpen}
          onCancel={() =>
            setConfirmDialog({ isOpen: false, filename: "", id: "", size: 0 })
          }
          onConfirm={() => void handleConfirmDeleteAttachment()}
        />
      </>
    );
  } else {
    return null;
  }
};

export default LeaveAttachments;
